<template>
  <div id="chart-wave">
    <highcharts v-if="activeChart" :options="chartOptions" :callback="onLoad"></highcharts>
  </div>
</template>

<script>
import {createNamespacedHelpers} from "vuex";
import {Chart} from 'highcharts-vue'
import moment from "moment-timezone";

// Store
const {
  mapState: mapStateOfLiveMap,
  mapMutations: mapMutationsOfLiveMap
} = createNamespacedHelpers("liveMap");

export default {
  components: {
    highcharts: Chart
  },
  props: {
    waveData: null,
  },
  data() {
    return {
      chart: null,
      activeChart: false,
      waveDirection: null,
      waveDirectionPathIds: [],
      chartOptions: {
        chart: {
          plotBorderWidth: 1,
          height: 400,
          alignTicks: false,
          scrollablePlotArea: {
            minWidth: 2000
          },
          spacingBottom: 50,
        },
        title: {
          text: null
        },
        legend: {
          enabled: false
        },
        tooltip: {
          outside: false,
          shared: true,
          useHTML: true,
          // backgroundColor: '#2D9CDB',
          borderColor: '#2D9CDB',
          formatter: function () {
            let time = moment(this.x).format("MM/DD(dd) HH:mm")
            let wavePeriod = `：${this.points[1].y}秒`
            let waveHeight = `：${this.points[0].y}m`
            return '<p><b>' + time + '</b></p><p>波高<b>' + waveHeight + '</b></p><p>波周期<b>' + wavePeriod + '</b></p>'
          },
          style: {
            // color: '#F2F2F2',
            fontSize: '12px'
          }
        },
        series: [
          {
            yAxis: 0,
            name: '波高(m)',
            type: 'column',
            pointStart: 0,
            pointInterval: 3600 * 1000,
            data: [],
            color: "#56CCF2"
          },
          {
            yAxis: 1,
            name: '波周期(秒)',
            type: 'spline',
            pointStart: 0,
            pointInterval: 3600 * 1000,
            data: [],
            color: "#2F80ED"
          }
        ],
        xAxis: [
          {
            crosshair: {
              color: "#D5EBF8",
              width: 30,
              className: "offing-wave-point-detail-crosshair"
            },
            type: 'datetime',
            startOnTick: false,
            endOnTick: false,
            maxPadding: 0,
            minPadding: 0,
            tickInterval: 3600 * 1000,
            offset: 30,
            showLastLabel: false,
            tickLength: -30,
            labels: {
              useHTML: true,
              formatter: function () {
                let time = moment(this.value).format('HH')
                return `<p>${time}</p>`
              },
            },
          },
          {
            linkedTo: 0,
            type: 'datetime',
            startOnTick: false,
            endOnTick: false,
            maxPadding: 0,
            minPadding: 0,
            tickInterval: 3600 * 1000,
            labels: {
              overflow: "allow",
              autoRotation: false,
              align: "Left",
              formatter: function () {
                let startOfDay = moment(this.value).startOf('days')
                if (startOfDay.isSame(moment(this.value))) return moment(this.value).format('M/D');
              },
              style: {
                fontSize: '12px'
              },
              x: -5,
              y: 360,
            },
            opposite: true,
            tickLength: 0,
            gridLineWidth: 1
          }],
        yAxis: [
          {
            opposite: false,
            title: {
              text: "<p>波高<p/><p>(m)<p/>",
              rotation: 0,
              offset: 50,
              y: -80,
              useHTML: true,
              style: {
                fontSize: '12px',
                fontWeight: 'bold'
              }
            },
            min: 0,
            max: 5,
            labels: {
              formatter: function () {
                return Number(this.value).toFixed(1)
              },
              style: {
                fontSize: '10px',
                fontWeight: '500'
              },
              x: -10
            },
            maxPadding: 0.3,
            minRange: 8,
            tickInterval: 0.5,
            gridLineWidth: 0
          },
          {
            title: {
              text: "<p>周期<p/><p>(秒)<p/>",
              rotation: 0,
              offset: 50,
              y: -80,
              useHTML: true,
              style: {
                fontSize: '12px',
                fontWeight: 'bold'
              }
            },
            min: 0,
            max: 15.0,
            labels: {
              formatter: function () {
                return Number(this.value).toFixed(1)
              },
              style: {
                fontSize: '10px',
                fontWeight: '500'
              },
              align: 'left',
              x: 10
            },
            maxPadding: 0.3,
            minRange: 8,
            tickInterval: 1.5,
            opposite: true
          }
        ],
      }
    };
  },
  watch: {
    waveData(data) {
      if (data) {
        this.waveDirection = data.waveDirection
        let initialTime = moment(data.initialTime, 'YYYY-MM-DD HH:mm').valueOf()
        if (!this.chart) {
          this.chartOptions.series[0].pointStart = initialTime
          this.chartOptions.series[1].pointStart = initialTime
          this.chartOptions.series[0].data = data.waveHeight
          this.chartOptions.series[1].data = data.wavePeriod
          this.activeChart = true
        } else {
          // debugger
          if (this.waveDirectionPathIds.length !== 0) {
            this.waveDirectionPathIds.forEach((id) => {
              let dom = document.getElementById(id);
              if(dom) {
                dom.remove()
              }
            })
            this.waveDirectionPathIds = []
          }
          this.chart.series[0].pointStart = initialTime
          this.chart.series[1].pointStart = initialTime
          this.chart.series[0].setData(data.waveHeight, true, true)
          this.chart.series[1].setData(data.wavePeriod, true, true)
          this.renderWaveDirectionIcon()
        }
      }

    }
  },
  computed: {
    ...mapStateOfLiveMap({})
  },
  methods: {
    ...mapMutationsOfLiveMap([]),
    onLoad(chart) {
      this.chart = chart
      this.renderWaveDirectionIcon()
    },
    renderWaveDirectionIcon() {
      let xAxis = this.chart.xAxis[0],
        x,
        pos,
        max,
        isLast,
        i;

      for (pos = xAxis.min, max = moment(xAxis.min).add(3, "d"), i = 0; pos <= max + 36e5; pos += 36e5, i += 1) {
        isLast = pos === max + 36e5;
        x = Math.round(xAxis.toPixels(pos)) + (isLast ? 0.5 : -0.5);
        let pathId = `wave-direction-path-${this.waveDirection[i - 1]}`
        this.waveDirectionPathIds.push(pathId)
        this.chart.renderer
          .path([
              "M", 5.7741, 0.369188,
              "C", 5.37404, -0.11818, 4.62827, -0.11818, 4.22821, 0.369189,
              "L", 0.228955, 5.24126,
              "C", -0.306778, 5.89392, 0.157528, 6.87574, 1.0019, 6.87574,
              "H", 2.80195,
              "L", 2.80195, 20,
              "C", 2.80195, 20.5523, 3.24966, 21.0001, 3.80195, 21.0001,
              "H", 6.19773,
              "C", 6.75002, 21.0001, 7.19773, 20.5523, 7.19773, 20,
              "L", 7.19773, 6.87574,
              "L", 9.00042, 6.87574,
              "C", 9.84479, 6.87574, 10.3091, 5.89392, 9.77336, 5.24126,
              "L", 5.7741, 0.369188,
              "Z"
            ]
          )
          .attr({
            class: "highcharts-wind",
            translateX: x + 6,
            translateY: this.chart.plotTop + this.chart.plotHeight + 4,
            rotation: this.waveDirection[i - 1] + 180,
            rotationOriginX: 5,
            rotationOriginY: 12,
            fill: "#2D9CDB",
            id: pathId
          })
          .add();
      }
    }
  },
  mounted() {
  },
  created() {},
};
</script>

<style lang="scss">
</style>
